import { useState } from 'react';
import type { OpportunityCpqQuoteLink } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import {
  useCpqQuoteTemplates,
  useCreateOpportunityCpqQuoteFromTemplate,
  useLinkOpportunityCpqQuote,
  useOpportunityCpqQuote,
  useSyncOpportunityCpqQuote,
  useUnlinkOpportunityCpqQuote,
} from './api.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function formatTotal(value: string | null): string {
  if (value == null || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? currency.format(n) : value;
}

function openQuote(link: OpportunityCpqQuoteLink): void {
  if (link.launchUrl) window.open(link.launchUrl, '_blank', 'noopener,noreferrer');
}

/**
 * Shows the ConnectWise CPQ (Sell) quote linked to an opportunity, with an "Open quote"
 * button (opens the quote directly in ConnectWise CPQ / Quosal) plus link/unlink controls.
 * CW-synced opportunities auto-resolve by cwId; native opportunities link by quote number.
 */
export function OpportunityCpqQuoteSection({
  opportunityId,
  canEdit,
}: {
  opportunityId: string;
  canEdit: boolean;
}): JSX.Element {
  const query = useOpportunityCpqQuote(opportunityId);
  const linkMutation = useLinkOpportunityCpqQuote(opportunityId);
  const unlinkMutation = useUnlinkOpportunityCpqQuote(opportunityId);
  const syncMutation = useSyncOpportunityCpqQuote(opportunityId);
  const createMutation = useCreateOpportunityCpqQuoteFromTemplate(opportunityId);

  const [showLink, setShowLink] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [showSync, setShowSync] = useState(false);
  const [includeZeroQty, setIncludeZeroQty] = useState(false);
  const [includeNotPrinted, setIncludeNotPrinted] = useState(false);
  const [includeDeselectedOptions, setIncludeDeselectedOptions] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const templates = useCpqQuoteTemplates(showCreate);

  const link = query.data;

  const actionError =
    linkMutation.error instanceof ApiError
      ? linkMutation.error.message
      : unlinkMutation.error instanceof ApiError
        ? unlinkMutation.error.message
        : null;

  const handleLink = (): void => {
    const n = Number(quoteNumber.trim());
    if (!Number.isInteger(n) || n <= 0) return;
    linkMutation.mutate(n, {
      onSuccess: () => {
        setShowLink(false);
        setQuoteNumber('');
      },
    });
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">ConnectWise CPQ quote</h3>
        <Button variant="ghost" onClick={() => void query.refetch()} isLoading={query.isFetching}>
          Refresh
        </Button>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-slate-500">Looking up the linked quote…</p>
      ) : query.isError ? (
        <p className="text-sm text-rose-600">Could not load the CPQ quote.</p>
      ) : link ? (
        <div className="space-y-3">
          {!link.configured ? (
            <p className="text-xs text-amber-600">
              ConnectWise CPQ is not configured. An admin can set it up under Integrations →
              ConnectWise CPQ.
            </p>
          ) : null}

          {link.linked ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="font-semibold text-slate-800">Quote #{link.quoteNumber ?? '—'}</span>
              {link.quoteStatus ? <Badge tone="blue">{link.quoteStatus}</Badge> : null}
              {link.resolvedBy === 'auto' ? (
                <span className="text-xs text-slate-400">(matched by ConnectWise ID)</span>
              ) : null}
              {link.quoteName ? <span className="text-slate-600">{link.quoteName}</span> : null}
              {link.total ? (
                <span className="text-slate-500">· {formatTotal(link.total)}</span>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              No CPQ quote is linked to this opportunity yet.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => openQuote(link)} disabled={!link.launchUrl}>
              Open quote
            </Button>
            {canEdit && link.configured ? (
              <Button variant="secondary" onClick={() => setShowCreate(true)}>
                Start new quote
              </Button>
            ) : null}
            {canEdit && link.configured && link.linked ? (
              <Button variant="secondary" onClick={() => setShowSync(true)}>
                Sync from CPQ
              </Button>
            ) : null}
            {canEdit && link.configured ? (
              <>
                <Button variant="secondary" onClick={() => setShowLink((v) => !v)}>
                  {link.linked ? 'Link a different quote' : 'Link a quote'}
                </Button>
                {link.linked ? (
                  <Button
                    variant="ghost"
                    onClick={() => unlinkMutation.mutate()}
                    isLoading={unlinkMutation.isPending}
                  >
                    Unlink
                  </Button>
                ) : null}
              </>
            ) : null}
          </div>

          {syncMutation.data ? (
            <p className="text-sm text-emerald-600">
              Synced {syncMutation.data.productsReplaced} product
              {syncMutation.data.productsReplaced === 1 ? '' : 's'} from quote #
              {syncMutation.data.quoteNumber ?? '—'}
              {syncMutation.data.skipped > 0 ? ` (skipped ${syncMutation.data.skipped})` : ''}
              {syncMutation.data.statusSetToOrderApproved ? ' · status set to Order Approved' : ''}.
            </p>
          ) : null}

          {createMutation.data ? (
            <p className="text-sm text-emerald-600">
              Created quote #{createMutation.data.link.quoteNumber ?? '—'} from template
              {createMutation.data.crmLinked ? ' · linked in CPQ' : ''}
              {createMutation.data.customerPushed ? ' · company & contact pushed' : ''}.
            </p>
          ) : null}

          {showLink ? (
            <div className="flex flex-wrap items-end gap-2 rounded-md border border-slate-200 bg-white p-3">
              <label className="text-sm">
                <span className="mb-1 block font-medium text-slate-600">Quote number</span>
                <input
                  type="number"
                  min={1}
                  value={quoteNumber}
                  onChange={(e) => setQuoteNumber(e.target.value)}
                  placeholder="e.g. 1682"
                  className="w-40 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
                />
              </label>
              <Button
                onClick={handleLink}
                isLoading={linkMutation.isPending}
                disabled={!quoteNumber.trim()}
              >
                Link
              </Button>
              <Button variant="ghost" onClick={() => setShowLink(false)}>
                Cancel
              </Button>
            </div>
          ) : null}

          {actionError ? <p className="text-sm text-rose-600">{actionError}</p> : null}
        </div>
      ) : null}

      <Modal
        isOpen={showSync}
        onClose={() => setShowSync(false)}
        title="Sync products from CPQ"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowSync(false)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                syncMutation.mutate(
                  { includeZeroQty, includeNotPrinted, includeDeselectedOptions },
                  { onSuccess: () => setShowSync(false) },
                )
              }
              isLoading={syncMutation.isPending}
            >
              Sync
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm">
          <p className="text-slate-600">
            This <strong>overwrites all products</strong> on this opportunity with the line items
            from the linked CPQ quote.
          </p>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeZeroQty}
              onChange={(e) => setIncludeZeroQty(e.target.checked)}
            />
            Include qty 0 items
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeNotPrinted}
              onChange={(e) => setIncludeNotPrinted(e.target.checked)}
            />
            Include not-printed items
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeDeselectedOptions}
              onChange={(e) => setIncludeDeselectedOptions(e.target.checked)}
            />
            Include deselected option items
          </label>
          {syncMutation.error ? (
            <p className="text-rose-600">
              {syncMutation.error instanceof ApiError
                ? syncMutation.error.message
                : 'Failed to sync products from CPQ.'}
            </p>
          ) : null}
        </div>
      </Modal>

      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Start a new CPQ quote"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                createMutation.mutate(templateId, {
                  onSuccess: () => {
                    setShowCreate(false);
                    setTemplateId('');
                  },
                })
              }
              isLoading={createMutation.isPending}
              disabled={!templateId}
            >
              Create quote
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm">
          <p className="text-slate-600">
            This copies a ConnectWise CPQ template into a <strong>new quote</strong>, links it back
            to this opportunity, and pushes the customer&apos;s company name and the opportunity
            contact onto the quote.
          </p>
          {link?.linked ? (
            <p className="text-xs text-amber-600">
              A quote (#{link.quoteNumber ?? '—'}) is already linked. Creating a new one replaces
              the link on this opportunity.
            </p>
          ) : null}
          <label className="block">
            <span className="mb-1 block font-medium text-slate-600">Template</span>
            {templates.isLoading ? (
              <p className="text-slate-500">Loading templates…</p>
            ) : templates.isError ? (
              <p className="text-rose-600">
                {templates.error instanceof ApiError
                  ? templates.error.message
                  : 'Could not load CPQ templates.'}
              </p>
            ) : (templates.data ?? []).length === 0 ? (
              <p className="text-slate-500">No CPQ templates were found.</p>
            ) : (
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
              >
                <option value="">Select a template…</option>
                {(templates.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            )}
          </label>
          {createMutation.error ? (
            <p className="text-rose-600">
              {createMutation.error instanceof ApiError
                ? createMutation.error.message
                : 'Failed to create the quote from the template.'}
            </p>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
